import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "@/config/firebase";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, increment, orderBy, limit, deleteDoc, runTransaction } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Beer, Clock, MapPin, Users, Trophy, Pizza, LogOut, LogIn, History, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const now = () => new Date();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);
const SESSION_TIMEOUT_MINUTES = 30; // Session endet nach 30 Min Inaktivität

// ========================================
// 🎉 KIOSK V2.0 - KOMPLETT OHNE GPS! 🎉
// ========================================
// Hauptänderungen:
// - KEIN GPS mehr - Session-Beitritt ohne Standort-Check
// - Session startet erst bei 3 Nutzern (nicht 2)
// - Inaktivitäts-Tracking: 20 Min → Timer pausiert → "Bin noch da" Button
// - Auto-End wenn nur noch 1 Spieler übrig
// - Katastrophen-Alarm bei Session-Start (Sound/Vibration)
// - Session-Naming mit Datumsanhang

export default function Kiosk() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [mvpVote, setMvpVote] = useState("");
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [sessionLocationName, setSessionLocationName] = useState("");
  const [sessionDuration, setSessionDuration] = useState(0);
  const [mvpUnlocksIn, setMvpUnlocksIn] = useState(null);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const lastNotificationTime = useRef(0);
  
  // 🆕 Inaktivitäts-Tracking
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [myInactiveMinutes, setMyInactiveMinutes] = useState(0);
  const [sessionInactiveMinutes, setSessionInactiveMinutes] = useState(0);
  const sessionTimeoutRef = useRef(null);
  
  // 🆕 Trigger für Live-Updates der Bier/h Berechnung
  const [, forceUpdate] = useState(0);
  
  // 🆕 Check-in/out Tracking
  const [userPresence, setUserPresence] = useState({});
  const [myCheckedIn, setMyCheckedIn] = useState(false);
  const [myTotalMinutes, setMyTotalMinutes] = useState(0);
  const [myResponsibilityPoints, setMyResponsibilityPoints] = useState(0);
  const inactivityTimerRef = useRef(null);
  
  // Pizza Pazza Bestellung
  const [showPizzaModal, setShowPizzaModal] = useState(false);
  const [pizzaOrder, setPizzaOrder] = useState("Sandro Spezial");
  const [pizzaCustom, setPizzaCustom] = useState("");
  const [pizzaCount, setPizzaCount] = useState(1);

  // ========================================
  // LOAD USERS
  // ========================================
  useEffect(()=>{
    (async()=>{
      const us = await getDocs(collection(db, "users"));
      setUsers(us.docs.map(d=>({ id:d.id, ...d.data()})));
    })();
  },[]);

  // ========================================
  // LISTEN TO SESSIONS
  // ========================================
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "kiosk_sessions"), (snap) => {
      const items = snap.docs.map(d=>({ id:d.id, ...d.data()}));
      const active = items.find(s => !s.end_time);
      setActiveSession(active || null);
      setSessions(items.slice(-10).reverse());
    });
    return () => unsub();
  }, [user]);

  // ========================================
  // 🆕 LISTEN TO KIOSK ALARMS (FÜR ALLE USER!)
  // ========================================
  useEffect(() => {
    if (!user) return;
    
    // Höre auf neue Alarme (nur die letzten 5 Minuten)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60000);
    const q = query(
      collection(db, "kiosk_alarms"),
      where("created_at", ">=", fiveMinutesAgo),
      orderBy("created_at", "desc"),
      limit(10)
    );
    
    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === "added") {
          const alarm = change.doc.data();
          
          // Spiele Katastrophen-Alarm für ALLE User (außer dem der den Alarm ausgelöst hat)
          if (alarm.triggered_by !== user.uid) {
            console.log('🚨 KATASTROPHEN-ALARM empfangen!');
            playCatastropheAlarm();
          }
        }
      });
    });
    
    return () => unsub();
  }, [user]);

  // ========================================
  // 🆕 LISTEN TO PRESENCE DATA (INDIVIDUELLE MINUTEN)
  // ========================================
  useEffect(() => {
    if (!activeSession || !user) return;
    
    const q = query(
      collection(db, "kiosk_presence"),
      where("session_id", "==", activeSession.id)
    );
    
    const unsub = onSnapshot(q, (snap) => {
      const presenceData = {};
      
      snap.docs.forEach(doc => {
        const data = doc.data();
        presenceData[data.user_id] = {
          check_ins: data.check_ins || [],
          total_minutes: data.total_minutes || 0,
          responsibility_points: data.responsibility_points || 0,
          status: data.status || 'active',
          last_activity: data.last_activity
        };
        
        // Update my own status
        if (data.user_id === user.uid) {
          const checkIns = data.check_ins || [];
          const isCheckedIn = checkIns.length > 0 && checkIns[checkIns.length - 1].check_out_time === null;
          setMyCheckedIn(isCheckedIn);
          setMyTotalMinutes(data.total_minutes || 0);
          setMyResponsibilityPoints(data.responsibility_points || 0);
          
          // Start timer if checked in
          if (isCheckedIn && !inactivityTimerRef.current) {
            startInactivityTimer();
          }
        }
      });
      
      setUserPresence(presenceData);
    });
    
    return () => {
      unsub();
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [activeSession, user]);

  // ========================================
  // 🆕 CHECK IF SESSION SHOULD AUTO-END (NUR 1 PERSON ÜBRIG)
  // ========================================
  useEffect(() => {
    if (!activeSession || !user) return;
    if (activeSession.status !== 'active') return;
    
    // Count checked-in users
    const checkedInCount = Object.values(userPresence).filter(p => 
      p.status === 'active' && 
      p.check_ins.length > 0 && 
      p.check_ins[p.check_ins.length - 1].check_out_time === null
    ).length;
    
    // If only 1 person left, auto-end session
    if (checkedInCount === 1) {
      console.log('⚠️ Nur noch 1 Person in Session - Auto-Ende!');
      
      // Verzögern um sicherzustellen, dass alle Updates durch sind
      const timer = setTimeout(async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/kiosk/end_session?session_id=${activeSession.id}&user_id=${user.uid}`, {
            method: 'POST'
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Session auto-ended:', data);
            alert('Session automatisch beendet - nur noch 1 Person übrig!');
          }
        } catch (error) {
          console.error('Auto-end failed:', error);
        }
      }, 3000); // 3 Sekunden Verzögerung
      
      return () => clearTimeout(timer);
    }
  }, [userPresence, activeSession, user]);

  // ========================================
  // SESSION DURATION TIMER & LIVE UPDATES
  // ========================================
  useEffect(() => {
    if (!activeSession) return;
    
    const interval = setInterval(() => {
      // Zeit läuft erst wenn Session aktiv ist (3+ User)
      if (activeSession.status !== 'active' || !activeSession.active_since) {
        setSessionDuration(0);
        setMvpUnlocksIn(null);
        return;
      }
      
      const activeSince = activeSession.active_since?.toDate?.() || new Date();
      const elapsed = Math.floor((Date.now() - activeSince.getTime()) / 1000); // in seconds
      setSessionDuration(elapsed);
      
      // 🆕 Force re-render für live Bier/h Updates
      forceUpdate(prev => prev + 1);
      
      // MVP unlock countdown - nach 30 Minuten freigeschaltet
      const mvpUnlockTime = 30 * 60; // 1800 seconds (30 Minuten)
      if (elapsed < mvpUnlockTime) {
        setMvpUnlocksIn(mvpUnlockTime - elapsed);
      } else {
        setMvpUnlocksIn(0);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeSession]);
  
  // ========================================
  // 🆕 INAKTIVITÄTS-TRACKING
  // ========================================
  useEffect(() => {
    if (!activeSession || activeSession.status !== 'active' || !user) return;
    
    const interval = setInterval(async () => {
      const lastActivityTime = activeSession.last_activity?.[user.uid]?.toDate?.();
      if (!lastActivityTime) return;
      
      const inactiveMs = Date.now() - lastActivityTime.getTime();
      const inactiveMin = Math.floor(inactiveMs / 60000);
      
      setMyInactiveMinutes(inactiveMin);
      
      // Nach 20 Min Inaktivität → Warnung anzeigen
      if (inactiveMin >= 20 && !showInactivityWarning) {
        setShowInactivityWarning(true);
        playSound('alert');
        
        // Push Notification senden (falls möglich)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('⚠️ Kiosk Inaktivität', {
            body: 'Du warst 20 Minuten inaktiv! Drücke "Bin noch da" um weiterzumachen.',
            icon: '/logo.png'
          });
        }
      }
    }, 30000); // Check alle 30 Sekunden
    
    return () => clearInterval(interval);
  }, [activeSession, user, showInactivityWarning]);
  
  // ========================================
  // 🆕 SESSION TIMEOUT NACH 30 MIN INAKTIVITÄT
  // ========================================
  useEffect(() => {
    if (!activeSession || activeSession.status !== 'active') {
      setSessionInactiveMinutes(0);
      return;
    }
    
    const interval = setInterval(async () => {
      // Finde die letzte Aktivität aller User
      const lastActivities = activeSession.last_activity || {};
      const activityTimes = Object.values(lastActivities)
        .map(t => t?.toDate?.()?.getTime() || 0)
        .filter(t => t > 0);
      
      if (activityTimes.length === 0) return;
      
      const mostRecentActivity = Math.max(...activityTimes);
      const inactiveMs = Date.now() - mostRecentActivity;
      const inactiveMin = Math.floor(inactiveMs / 60000);
      
      setSessionInactiveMinutes(inactiveMin);
      
      // Nach 30 Min KEINE Aktivität → Session automatisch beenden
      if (inactiveMin >= SESSION_TIMEOUT_MINUTES) {
        console.log(`⏰ Session-Timeout: ${SESSION_TIMEOUT_MINUTES} Minuten ohne Aktivität!`);
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/kiosk/end_session?session_id=${activeSession.id}&user_id=${user.uid}&reason=timeout`, {
            method: 'POST'
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Session auto-ended due to inactivity:', data);
            
            // Benachrichtigung
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('⏰ Session beendet', {
                body: `Die Kiosk-Session wurde nach ${SESSION_TIMEOUT_MINUTES} Minuten Inaktivität automatisch beendet.`,
                icon: '/logo.png'
              });
            }
            
            alert(`⏰ Session automatisch beendet!\n\nGrund: ${SESSION_TIMEOUT_MINUTES} Minuten ohne Aktivität.`);
          }
        } catch (error) {
          console.error('Session timeout error:', error);
        }
      }
    }, 60000); // Check jede Minute
    
    return () => clearInterval(interval);
  }, [activeSession, user]);
  
  // ========================================
  // 🆕 AUTO-END BEI NUR 1 SPIELER
  // ========================================
  useEffect(() => {
    if (!activeSession || activeSession.status !== 'active') return;
    
    const interval = setInterval(async () => {
      const currentUserIds = activeSession.user_ids || [];
      
      // Wenn nur noch 1 Spieler → Session automatisch beenden
      if (currentUserIds.length === 1) {
        console.log('⚠️ Nur noch 1 Spieler → Session wird automatisch beendet');
        
        try {
          await updateDoc(doc(db, "kiosk_sessions", activeSession.id), {
            end_time: serverTimestamp(),
            status: 'ended'
          });
          
          // Benachrichtigung an letzten Spieler
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('🏁 Session beendet', {
              body: 'Du warst der letzte Spieler. Die Session wurde automatisch beendet.',
              icon: '/logo.png'
            });
          }
        } catch (error) {
          console.error('Fehler beim Auto-End:', error);
        }
      }
    }, 60000); // Check alle 60 Sekunden
    
    return () => clearInterval(interval);
  }, [activeSession]);

  // ========================================
  // "BIN NOCH DA" BUTTON
  // ========================================
  const imStillHere = async () => {
    if (!activeSession || !user) return;
    
    try {
      const ref = doc(db, "kiosk_sessions", activeSession.id);
      await updateDoc(ref, {
        [`last_activity.${user.uid}`]: serverTimestamp()
      });
      
      setShowInactivityWarning(false);
      setMyInactiveMinutes(0);
      playSound('hoppa');
      alert('✅ Willkommen zurück!');
    } catch (error) {
      console.error('Fehler beim Update:', error);
      alert('Fehler beim Update der Aktivität');
    }
  };

  // ========================================
  // 🆕 CHECK OUT (MANUELL)
  // ========================================
  const checkOut = async () => {
    if (!activeSession || !user) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/kiosk/check_out?session_id=${activeSession.id}&user_id=${user.uid}&manual=true`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Checkout failed');
      
      const data = await response.json();
      setMyCheckedIn(false);
      setMyTotalMinutes(data.total_minutes || 0);
      setMyResponsibilityPoints(data.responsibility_points || 0);
      
      // Clear inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      alert(`✅ Ausgecheckt! +1 Zurechnungsfähigkeitspunkt\nGesamtzeit: ${Math.round(data.total_minutes)} Min`);
    } catch (error) {
      console.error('Checkout error:', error);
      alert('❌ Fehler beim Auschecken');
    }
  };

  // ========================================
  // 🆕 CHECK IN (WIEDER EINSTEIGEN)
  // ========================================
  const checkIn = async () => {
    if (!activeSession || !user) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/kiosk/check_in?session_id=${activeSession.id}&user_id=${user.uid}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Check-in failed');
      
      const data = await response.json();
      
      if (data.already_checked_in) {
        alert('Du bist bereits eingecheckt!');
        return;
      }
      
      setMyCheckedIn(true);
      
      // Start inactivity timer (60 minutes)
      startInactivityTimer();
      
      alert('✅ Eingecheckt! Deine Zeit läuft.');
    } catch (error) {
      console.error('Check-in error:', error);
      alert('❌ Fehler beim Einchecken');
    }
  };

  // ========================================
  // 🆕 AUTO CHECK-OUT (NACH 60 MIN INAKTIVITÄT)
  // ========================================
  const autoCheckOut = async () => {
    if (!activeSession || !user) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/kiosk/auto_check_out?session_id=${activeSession.id}&user_id=${user.uid}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Auto checkout failed');
      
      const data = await response.json();
      setMyCheckedIn(false);
      setMyTotalMinutes(data.total_minutes || 0);
      
      alert(`⏰ Auto-Checkout nach 60 Min Inaktivität!\n-30 Min Penalty\nGesamtzeit: ${Math.round(data.total_minutes)} Min`);
    } catch (error) {
      console.error('Auto checkout error:', error);
    }
  };

  // ========================================
  // 🆕 START INACTIVITY TIMER (60 MINUTEN)
  // ========================================
  const startInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Set timer for 60 minutes
    inactivityTimerRef.current = setTimeout(() => {
      autoCheckOut();
    }, 60 * 60 * 1000); // 60 minutes
  };

  // ========================================
  // FORMAT DURATION AS MM:SS or HH:MM:SS
  // ========================================
  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ========================================
  // 🆕 START NEW SESSION - ÜBER BACKEND API!
  // ========================================
  const startSession = async () => {
    if (!sessionLocationName.trim()) {
      alert("Bitte gib einen Namen für den Ort ein!");
      return;
    }
    
    const dateStr = new Date().toLocaleDateString('de-DE');
    const fullLocationName = `${sessionLocationName.trim()} (${dateStr})`;
    
    try {
      // Backend API Call - erstellt Session + Presence automatisch
      const response = await fetch(`${BACKEND_URL}/api/kiosk/start_session?user_id=${user.uid}&location_name=${encodeURIComponent(fullLocationName)}&latitude=0&longitude=0`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Session start failed');
      }
      
      const data = await response.json();
      console.log('✅ Session started via Backend:', data);
      
      playSound('hoppa');
      setShowStartConfirm(false);
      setSessionLocationName("");
      alert('✅ Session erstellt! Warte auf 2 weitere Spieler.');
    } catch (error) {
      console.error('Session start error:', error);
      alert(`❌ Fehler beim Session-Start: ${error.message}`);
    }
  };

  // ========================================
  // ADMIN: MANUALLY END SESSION - ÜBER BACKEND API!
  // ========================================
  const endSession = async () => {
    if (!activeSession) {
      alert("Keine aktive Session!");
      return;
    }
    setShowEndConfirm(true);
  };
  
  const confirmEndSession = async () => {
    if (!activeSession || !user) return;
    
    try {
      // Backend API Call - berechnet individuelle Minuten + erstellt kiosk_day
      const response = await fetch(`${BACKEND_URL}/api/kiosk/end_session?session_id=${activeSession.id}&user_id=${user.uid}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'End session failed');
      }
      
      const data = await response.json();
      console.log('✅ Session ended via Backend:', data);
      
      setShowEndConfirm(false);
      alert(`✅ Session beendet!\n${data.user_summaries_created} User-Zusammenfassungen erstellt.\nDauer: ${data.duration_minutes} Min`);
    } catch (e) {
      alert(`Fehler beim Beenden: ${e.message}`);
      console.error(e);
    }
  };

  // ========================================
  // 🆕 JOIN SESSION - ÜBER BACKEND API!
  // ========================================
  const joinSession = async () => {
    // 🔒 User Authentication Check
    if (!user) {
      alert("Du musst angemeldet sein um beizutreten!");
      return;
    }
    
    if (!activeSession || activeSession.status === 'ended') {
      alert("Keine aktive Session zum Beitreten!");
      return;
    }
    
    if (activeSession.user_ids?.includes(user.uid)) {
      alert("Du bist bereits in der Session!");
      return;
    }
    
    console.log(`🍺 User ${user.uid} joining session ${activeSession.id}`);
    
    try {
      // Backend API Call - adds User + creates Presence automatisch
      const response = await fetch(`${BACKEND_URL}/api/kiosk/join_session?session_id=${activeSession.id}&user_id=${user.uid}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Join failed');
      }
      
      const data = await response.json();
      console.log('✅ Joined session via Backend:', data);
      
      // 🆕 KATASTROPHEN-ALARM bei Session-Start (wenn 3 User erreicht)!
      if (data.status === 'active' && data.user_count === 3) {
        // Lokalen Alarm spielen
        playCatastropheAlarm();
        
        // Alarm-Trigger in Firestore schreiben für ALLE User
        try {
          await addDoc(collection(db, "kiosk_alarms"), {
            session_id: activeSession.id,
            message: "🚨 KIOSK TIME! Session ist jetzt aktiv!",
            created_at: serverTimestamp(),
            triggered_by: user.uid
          });
          console.log('✅ Katastrophen-Alarm an alle User gesendet!');
        } catch (e) {
          console.error('Fehler beim Alarm-Trigger:', e);
        }
      }
      
      playSound('hoppa');
      alert(data.message || 'Session beigetreten!');
    } catch (error) {
      console.error("Join failed:", error);
      alert(`Fehler beim Beitreten: ${error.message}`);
    }
  };

  // ========================================
  // BEER TRACKING
  // ========================================
  const plusBeer = async () => {
    if (!activeSession) return;
    
    try {
      const ref = doc(db, "kiosk_sessions", activeSession.id);
      await updateDoc(ref, {
        [`beers_by_user.${user.uid}`]: increment(1),
        [`last_activity.${user.uid}`]: serverTimestamp() // 🆕 Update last_activity
      });
    } catch (error) {
      console.error("Beer tracking error:", error);
    }
  };
  
  const minusBeer = async () => {
    if (!activeSession) return;
    const currentBeers = activeSession.beers_by_user?.[user.uid] || 0;
    if (currentBeers <= 0) return;
    
    try {
      const ref = doc(db, "kiosk_sessions", activeSession.id);
      await updateDoc(ref, {
        [`beers_by_user.${user.uid}`]: increment(-1),
        [`last_activity.${user.uid}`]: serverTimestamp() // 🆕 Update last_activity
      });
    } catch (error) {
      console.error("Beer tracking error:", error);
    }
  };

  // ========================================
  // MVP VOTING
  // ========================================
  const submitMvpVote = async () => {
    if (!activeSession || !mvpVote) return;
    
    if (mvpVote === user.uid) {
      alert("Du kannst nicht für dich selbst voten! 🙈");
      return;
    }
    
    if (activeSession.user_votes?.[user.uid]) {
      alert("Du hast bereits gevoted! ✅");
      return;
    }
    
    try {
      const ref = doc(db, "kiosk_sessions", activeSession.id);
      await updateDoc(ref, {
        [`mvp_votes.${mvpVote}`]: increment(1),
        [`user_votes.${user.uid}`]: mvpVote,
        [`last_activity.${user.uid}`]: serverTimestamp() // 🆕 Update last_activity
      });
      
      alert("Dein Vote wurde gespeichert! 🏆");
      setMvpVote("");
    } catch (error) {
      console.error("MVP vote error:", error);
      alert("Fehler beim Voten");
    }
  };

  // ========================================
  // PIZZA PAZZA BESTELLUNG
  // ========================================
  const orderPizza = () => {
    const bestellung = pizzaOrder === "Etwas anderes" ? pizzaCustom : pizzaOrder;
    const smsText = `Guten Abend liebes Pizza Pazza Team, ich würde gerne ${pizzaCount} mal ${bestellung} bestellen und gleich abholen, ich stehe nebenan bei Ali am Kiosk. danke und bis gleich.`;
    
    const phoneNumber = "+4915202833111";
    const smsUrl = `sms:${phoneNumber}?body=${encodeURIComponent(smsText)}`;
    
    window.location.href = smsUrl;
    
    setShowPizzaModal(false);
    setPizzaOrder("Sandro Spezial");
    setPizzaCustom("");
    setPizzaCount(1);
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getUserName = (uid) => {
    const u = users.find(u => u.id === uid);
    return u?.displayName || u?.email?.split('@')[0] || 'Unbekannt';
  };

  const playSound = (soundName) => {
    try {
      const audio = new Audio(`/sounds/${soundName}.mp3`);
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (e) {
      console.log('Sound error:', e);
    }
  };
  
  // 🆕 KATASTROPHEN-ALARM: EXTREM LAUT & AUFFÄLLIG!
  const playCatastropheAlarm = () => {
    try {
      // Sound - MAXIMALE LAUTSTÄRKE!
      const audio = new Audio('/sounds/kiosktime.mp3');
      audio.volume = 1.0; // 100% Lautstärke!
      audio.play().catch(e => console.log('Alarm sound failed:', e));
      
      // Zweiter Sound für Extra-Lautstärke (falls verfügbar)
      setTimeout(() => {
        const audio2 = new Audio('/sounds/hoppa.mp3');
        audio2.volume = 1.0;
        audio2.play().catch(e => console.log('Second alarm sound failed:', e));
      }, 500);
      
      // EXTREME Vibration (falls unterstützt)
      if ('vibrate' in navigator) {
        // Langes, intensives Vibrations-Pattern (5 Sekunden!)
        navigator.vibrate([
          300, 100, 300, 100, 300, 100, 300, 100, 300, 100,
          400, 100, 400, 100, 400, 100, 400, 100, 400
        ]);
      }
      
      // Push Notification (falls erlaubt)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🚨 KIOSK TIME! 🚨', {
          body: 'Die Kiosk-Session ist jetzt aktiv! 🍺🎉',
          icon: '/logo.png',
          vibrate: [300, 100, 300, 100, 300],
          requireInteraction: true, // Bleibt sichtbar bis User klickt
          tag: 'kiosk-alarm' // Verhindert mehrfache Notifications
        });
      }
      
      // Visual Alert - GROSS & AUFFÄLLIG
      alert('🚨🚨🚨 KIOSK TIME! 🚨🚨🚨\n\n🍺 Die Session ist jetzt aktiv! 🍺\n\nGeh sofort zum Kiosk!');
    } catch (e) {
      console.log('Catastrophe alarm error:', e);
    }
  };

  const sendNotificationToAll = (message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('🍺 Kiosk Update', {
        body: message,
        icon: '/logo.png',
        vibrate: [200, 100, 200]
      });
    }
  };

  // Admin: Delete session
  const deleteSession = async () => {
    if (role !== 'admin' || !sessionToDelete) return;
    
    try {
      await deleteDoc(doc(db, "kiosk_sessions", sessionToDelete));
      alert("Session gelöscht! ✅");
      setSessionToDelete(null);
    } catch (error) {
      console.error("Delete error:", error);
      alert("Fehler beim Löschen");
    }
  };

  // ========================================
  // RENDER
  // ========================================
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
        <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-semibold flex items-center gap-2">
              <Beer size={18} className="text-amber-400" />
              Kiosk
            </h1>
          </div>
        </header>
        <div className="px-3 py-4">
          <Card className="bg-[#12090c] border-white/5">
            <CardContent className="py-6 text-center">
              <Beer size={32} className="mx-auto mb-3 text-rose-200/30" />
              <p className="text-rose-200/60">Bitte einloggen um den Kiosk zu nutzen.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const myBeers = activeSession?.beers_by_user?.[user.uid] || 0;
  const hasVoted = activeSession?.user_votes?.[user.uid];
  const mvpUnlocked = activeSession?.status === 'active' && mvpUnlocksIn !== null && mvpUnlocksIn <= 0;
  
  // 🆕 Admin Override für Testing
  const adminMvpOverride = role === 'admin';
  const canVoteMvp = (mvpUnlocked || adminMvpOverride) && activeSession?.status === 'active';

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold flex items-center gap-2">
                <Beer size={18} className="text-amber-400" />
                Kiosk v2.0
              </h1>
              <p className="text-xs text-rose-200/50">Session startet bei 3 Spielern</p>
            </div>
          </div>
          {activeSession && activeSession.status === 'active' && (
            <div className="text-right">
              <p className="text-xs text-rose-200/50">Session Zeit</p>
              <p className="text-lg font-mono font-bold text-amber-400">
                {formatDuration(sessionDuration)}
              </p>
            </div>
          )}
        </div>
      </header>

      <div className="px-3 py-4 space-y-3">
        {/* ========================================
            🆕 INAKTIVITÄTS-WARNUNG
            ======================================== */}
        {showInactivityWarning && (
          <Card className="bg-orange-900/30 border-orange-500/50 border-2 animate-pulse">
            <CardContent className="py-4">
              <h3 className="font-bold text-orange-300 mb-2 flex items-center gap-2">
                <Clock size={18} />
                Inaktivitäts-Warnung!
              </h3>
              <p className="text-sm mb-3 text-rose-200/80">
                Du warst {myInactiveMinutes} Minuten inaktiv. Deine Zeit ist pausiert.
              </p>
              <Button 
                onClick={imStillHere}
                className="w-full bg-green-600 hover:bg-green-700 font-bold"
              >
                Bin noch da!
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Session Timeout Warning */}
        {activeSession?.status === 'active' && sessionInactiveMinutes >= 20 && (
          <Card className="bg-red-900/30 border-red-500/50">
            <CardContent className="py-3">
              <p className="text-sm text-red-300 flex items-center gap-2">
                <Clock size={14} />
                Session endet in {SESSION_TIMEOUT_MINUTES - sessionInactiveMinutes} Min ohne Aktivität!
              </p>
            </CardContent>
          </Card>
        )}

        {/* ========================================
            ACTIVE SESSION
            ======================================== */}
        {activeSession ? (
          <Card className="bg-[#12090c] border-amber-500/30">
            <CardContent className="pt-4 space-y-4">
              {/* Session Info */}
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  activeSession.status === 'waiting' 
                    ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                    : 'bg-green-500/20 text-green-300 border border-green-500/30'
                }`}>
                  {activeSession.status === 'waiting' ? (
                    <>
                      <Clock size={14} />
                      Warte auf Spieler...
                    </>
                  ) : (
                    <>
                      <Beer size={14} />
                      Session läuft!
                    </>
                  )}
                </div>
                <p className="text-sm text-rose-200/60 mt-2 flex items-center justify-center gap-1">
                  <MapPin size={12} />
                  {activeSession.location_name}
                </p>
                <p className="text-xs text-rose-200/40 mt-1">
                  <Users size={11} className="inline mr-1" />
                  {activeSession.user_ids?.length || 0} Spieler {activeSession.status === 'waiting' && '(3 benötigt)'}
                </p>
              </div>

              {/* 🆕 JOIN SESSION BUTTON - wenn User nicht in Session */}
              {!activeSession.user_ids?.includes(user.uid) && activeSession.status !== 'ended' && (
                <Button 
                  onClick={joinSession}
                  className="w-full bg-green-600 hover:bg-green-700 font-bold py-5"
                  data-testid="join-session-btn"
                >
                  <Beer size={18} className="mr-2" />
                  Jetzt der Session beitreten!
                </Button>
              )}

              {/* Timer (nur wenn aktiv) - jetzt im Header */}

              {/* Meine Biere */}
              <div className="bg-white/5 p-4 rounded-lg border border-amber-500/20">
                <p className="text-xs text-rose-200/60 mb-2 text-center">Meine Biere:</p>
                <div className="flex items-center justify-center gap-4">
                  <Button 
                    onClick={minusBeer}
                    disabled={myBeers === 0 || activeSession.status !== 'active'}
                    className="bg-red-600/80 hover:bg-red-700 text-2xl w-14 h-14 p-0"
                  >
                    −
                  </Button>
                  <span className="text-5xl font-bold text-amber-400 min-w-[80px] text-center">
                    {myBeers}
                  </span>
                  <Button 
                    onClick={plusBeer}
                    disabled={activeSession.status !== 'active'}
                    className="bg-green-600/80 hover:bg-green-700 text-2xl w-14 h-14 p-0"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* 🆕 CHECK-IN / CHECK-OUT */}
              {activeSession.status === 'active' && activeSession.user_ids?.includes(user.uid) && (
                <div className="bg-white/5 p-4 rounded-lg border border-blue-500/20">
                  <p className="text-xs text-rose-200/60 mb-2 text-center flex items-center justify-center gap-1">
                    <Clock size={12} />
                    Zeit-Tracking
                  </p>
                  
                  <div className="flex gap-4 text-center mb-3 justify-center">
                    <div>
                      <span className="text-[10px] text-rose-200/50">Deine Zeit</span>
                      <p className="text-lg font-bold text-blue-400">{Math.round(myTotalMinutes)} Min</p>
                    </div>
                    {myResponsibilityPoints > 0 && (
                      <div>
                        <span className="text-[10px] text-rose-200/50">Verantwortung</span>
                        <p className="text-lg font-bold text-green-400">{myResponsibilityPoints} Pkt</p>
                      </div>
                    )}
                  </div>
                  
                  {myCheckedIn ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-green-400 text-xs">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        Du bist eingecheckt - Zeit läuft
                      </div>
                      <Button 
                        onClick={checkOut}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-sm"
                      >
                        <LogOut size={14} className="mr-2" />
                        Auschecken (+1 Verantwortungspunkt)
                      </Button>
                      <p className="text-[10px] text-rose-200/40 text-center">
                        Auto-Checkout nach 60 Min Inaktivität
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-orange-400 text-xs">
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        Du bist ausgecheckt - Zeit pausiert
                      </div>
                      <Button 
                        onClick={checkIn}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-sm"
                      >
                        <LogIn size={14} className="mr-2" />
                        Wieder einchecken
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* MVP Voting */}
              {activeSession.status === 'active' && (
                <div className="bg-white/5 p-4 rounded-lg border border-purple-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-rose-200/60 flex items-center gap-1">
                      <Trophy size={12} className="text-purple-400" />
                      MVP Vote
                    </p>
                    {!canVoteMvp && mvpUnlocksIn !== null && mvpUnlocksIn > 0 && (
                      <p className="text-[10px] text-rose-200/40">
                        Unlock in: {Math.floor(mvpUnlocksIn / 60)}:{String(mvpUnlocksIn % 60).padStart(2, '0')}
                      </p>
                    )}
                    {adminMvpOverride && !mvpUnlocked && (
                      <span className="text-[10px] bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded">Admin</span>
                    )}
                  </div>
                  
                  {hasVoted ? (
                    <p className="text-center text-green-400 text-sm py-2">
                      Du hast für {getUserName(hasVoted)} gevoted
                    </p>
                  ) : canVoteMvp ? (
                    <div className="space-y-2">
                      <select
                        value={mvpVote}
                        onChange={(e) => setMvpVote(e.target.value)}
                        className="w-full p-2 rounded-lg bg-white/5 border border-purple-500/30 text-sm focus:outline-none focus:border-purple-500/50"
                      >
                        <option value="">Wähle einen Spieler...</option>
                        {(activeSession.user_ids || [])
                          .filter(uid => uid !== user.uid)
                          .map(uid => (
                            <option key={uid} value={uid}>
                              {getUserName(uid)}
                            </option>
                          ))}
                      </select>
                      <Button 
                        onClick={submitMvpVote}
                        disabled={!mvpVote}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-sm"
                      >
                        <Trophy size={14} className="mr-2" />
                        Vote abgeben
                      </Button>
                    </div>
                  ) : (
                    <p className="text-center text-rose-200/40 py-2 text-xs">
                      MVP Voting nach 30 Min freigeschaltet
                    </p>
                  )}
                </div>
              )}

              {/* Pizza Pazza Button */}
              <Button 
                onClick={() => setShowPizzaModal(true)}
                className="w-full bg-orange-600/80 hover:bg-orange-700 text-sm"
              >
                <Pizza size={14} className="mr-2" />
                Pizza Pazza bestellen
              </Button>

              {/* User List */}
              <div className="border-t border-white/5 pt-4">
                <p className="text-xs text-rose-200/50 mb-2 flex items-center gap-1">
                  <Users size={12} />
                  Spieler in Session
                </p>
                <div className="space-y-1.5">
                  {(activeSession.user_ids || []).map(uid => {
                    const beers = activeSession.beers_by_user?.[uid] || 0;
                    const presence = userPresence[uid] || {};
                    const checkIns = presence.check_ins || [];
                    const isCheckedIn = checkIns.length > 0 && checkIns[checkIns.length - 1]?.check_out_time === null;
                    
                    let userMinutes = presence.total_minutes || 0;
                    
                    if (isCheckedIn && checkIns.length > 0) {
                      const lastCheckIn = checkIns[checkIns.length - 1];
                      const checkInTime = lastCheckIn.check_in_time?.toDate?.() || new Date();
                      const currentMinutes = Math.floor((Date.now() - checkInTime.getTime()) / 60000);
                      userMinutes += currentMinutes;
                    }
                    
                    const beerPerHour = userMinutes > 0 ? (beers / (userMinutes / 60)).toFixed(1) : '0.0';
                    const responsibilityPts = presence.responsibility_points || 0;
                    
                    return (
                      <div key={uid} className={`p-2 rounded-lg ${
                        !isCheckedIn ? 'bg-red-900/20 opacity-60' : 'bg-white/5'
                      }`}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium flex items-center gap-1.5">
                            {getUserName(uid)}
                            {uid === user.uid && <span className="text-[10px] text-rose-200/40">(Du)</span>}
                            {isCheckedIn ? (
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                            ) : (
                              <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                            )}
                          </span>
                          <span className="text-amber-400 font-bold text-sm flex items-center gap-1">
                            <Beer size={12} />
                            {beers}
                          </span>
                        </div>
                        
                        <div className="flex gap-3 text-[10px] text-rose-200/50 mt-1">
                          <span>{Math.round(userMinutes)} Min</span>
                          <span>{beerPerHour} Bier/h</span>
                          {responsibilityPts > 0 && <span className="text-green-400">{responsibilityPts} VP</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Admin: End Session */}
              {role === 'admin' && (
                <Button 
                  onClick={endSession}
                  className="w-full bg-red-600/80 hover:bg-red-700 text-sm"
                >
                  <Trash2 size={14} className="mr-2" />
                  Session beenden (Admin)
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          /* ========================================
              KEINE AKTIVE SESSION
              ======================================== */
          <Card className="bg-[#12090c] border-white/5">
            <CardContent className="pt-4 space-y-4">
              <div className="text-center py-4">
                <Beer size={40} className="mx-auto mb-3 text-amber-400/30" />
                <p className="text-rose-200/60 mb-4">
                  Keine aktive Session
                </p>
              </div>
              
              <Button 
                onClick={() => setShowStartConfirm(true)}
                className="w-full bg-green-600 hover:bg-green-700 py-5 font-bold"
                data-testid="start-session-btn"
              >
                <Beer size={18} className="mr-2" />
                Neue Session starten
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ========================================
            SESSION HISTORY
            ======================================== */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2 text-rose-200/70">
            <History size={14} />
            Session History
          </h2>
          {sessions.filter(s => s.end_time).length === 0 ? (
            <p className="text-xs text-rose-200/40 text-center py-4">Noch keine abgeschlossenen Sessions</p>
          ) : (
            sessions.filter(s => s.end_time).map(s => {
              const stats = {
                totalBeers: Object.values(s.beers_by_user || {}).reduce((a,b) => a+b, 0),
                totalMinutes: Math.floor((s.end_time?.toDate?.().getTime() - s.start_time?.toDate?.().getTime()) / 60000)
              };
              
              const topUsers = Object.entries(s.beers_by_user || {})
                .sort((a,b) => (b[1]||0) - (a[1]||0))
                .slice(0, 2)
                .map(([uid]) => uid);
              const isTop2 = topUsers.includes(user.uid);
              const isExpanded = expandedSession === s.id;
              
              return (
                <Card key={s.id} className={`bg-[#12090c] border-white/5 mb-2 ${isTop2 ? 'border-l-2 border-l-amber-500' : ''}`}>
                  <CardContent className="py-3">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedSession(isExpanded ? null : s.id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {new Date(s.start_time?.toDate?.()).toLocaleDateString('de-DE')}
                          <span className="text-rose-200/40 text-xs ml-2">
                            {s.user_ids?.length || 0} Spieler
                          </span>
                        </p>
                        <p className="text-xs text-rose-200/50 flex items-center gap-1">
                          <MapPin size={10} />
                          {s.location_name || 'Standort unbekannt'}
                        </p>
                        <div className="flex gap-3 text-xs text-rose-200/40 mt-1">
                          <span>{stats.totalBeers} Biere</span>
                          <span>{stats.totalMinutes} Min</span>
                          {isTop2 && <span className="text-amber-400">Top 2!</span>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {role === 'admin' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionToDelete(s.id);
                            }}
                            className="p-1.5 bg-red-900/30 hover:bg-red-900/50 rounded text-red-400"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                    
                    {/* Session Boxscore */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-xs font-semibold mb-2 text-rose-200/60">Boxscore</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-white/10 text-rose-200/50">
                                <th className="text-left py-1">Spieler</th>
                                <th className="text-center py-1">Biere</th>
                                <th className="text-center py-1">Min</th>
                                <th className="text-center py-1">B/h</th>
                                <th className="text-center py-1">MVP</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(s.user_ids || []).map(uid => {
                                const beers = s.beers_by_user?.[uid] || 0;
                                const minutes = s.minutes_by_user?.[uid] || s.duration_minutes || 0;
                                const beerPerHour = minutes > 0 ? (beers / (minutes / 60)).toFixed(1) : '0.0';
                                const mvpVotes = s.mvp_votes?.[uid] || 0;
                                
                                return (
                                  <tr key={uid} className="border-b border-white/5">
                                    <td className="py-1.5">{getUserName(uid)}</td>
                                    <td className="text-center text-amber-400 font-bold">{beers}</td>
                                    <td className="text-center text-rose-200/60">{Math.round(minutes)}</td>
                                    <td className="text-center text-rose-200/60">{beerPerHour}</td>
                                    <td className="text-center">{mvpVotes > 0 ? <Trophy size={12} className="inline text-purple-400" /> : '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* ========================================
            MODALS
            ======================================== */}
        
        {/* Start Session Modal */}
        {showStartConfirm && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <Card className="bg-[#12090c] border-green-500/30 max-w-sm w-full">
              <CardContent className="pt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Beer size={18} className="text-green-400" />
                  Neue Session starten
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-rose-200/60 block mb-1">
                      Wo bist du?
                    </label>
                    <input
                      type="text"
                      value={sessionLocationName}
                      onChange={(e) => setSessionLocationName(e.target.value)}
                      placeholder="z.B. Ali's Kiosk, Rheinufer"
                      className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500/50 text-sm"
                      autoFocus
                    />
                    <p className="text-[10px] text-rose-200/40 mt-1">
                      Datum wird automatisch hinzugefügt
                    </p>
                  </div>
                  
                  <div className="bg-yellow-900/20 border border-yellow-500/20 p-2 rounded-lg">
                    <p className="text-xs text-yellow-200/80">
                      Session startet bei 3 Spielern automatisch
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={startSession}
                    disabled={!sessionLocationName.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Starten
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowStartConfirm(false);
                      setSessionLocationName("");
                    }}
                    variant="outline"
                    className="flex-1 bg-white/5 border-white/10"
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* End Session Confirmation */}
        {showEndConfirm && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <Card className="bg-[#12090c] border-red-500/30 max-w-sm w-full">
              <CardContent className="pt-4">
                <h3 className="font-semibold mb-2 text-red-400">Session beenden?</h3>
                <p className="text-sm text-rose-200/60 mb-4">
                  Die aktive Session wird für alle beendet.
                </p>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={confirmEndSession}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Beenden
                  </Button>
                  <Button 
                    onClick={() => setShowEndConfirm(false)}
                    variant="outline"
                    className="flex-1 bg-white/5 border-white/10"
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Session Confirmation */}
        {sessionToDelete && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <Card className="bg-[#12090c] border-red-500/30 max-w-sm w-full">
              <CardContent className="pt-4">
                <h3 className="font-semibold mb-2 text-red-400">Session löschen?</h3>
                <p className="text-sm text-rose-200/60 mb-4">
                  Dies kann nicht rückgängig gemacht werden!
                </p>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={deleteSession}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Löschen
                  </Button>
                  <Button 
                    onClick={() => setSessionToDelete(null)}
                    variant="outline"
                    className="flex-1 bg-white/5 border-white/10"
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pizza Pazza Modal */}
        {showPizzaModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <Card className="bg-[#12090c] border-orange-500/30 max-w-sm w-full">
              <CardContent className="pt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Pizza size={18} className="text-orange-400" />
                  Pizza Pazza Bestellung
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-rose-200/60 block mb-1">Anzahl:</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={pizzaCount}
                      onChange={(e) => setPizzaCount(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full p-2 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-orange-500/50 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-rose-200/60 block mb-1">Bestellung:</label>
                    <select
                      value={pizzaOrder}
                      onChange={(e) => setPizzaOrder(e.target.value)}
                      className="w-full p-2 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-orange-500/50 text-sm"
                    >
                      <option value="Sandro Spezial">Sandro Spezial</option>
                      <option value="Etwas anderes">Etwas anderes</option>
                    </select>
                  </div>
                  
                  {pizzaOrder === "Etwas anderes" && (
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Deine Bestellung:</label>
                      <input
                        type="text"
                        placeholder="z.B. Pizza Margherita"
                        value={pizzaCustom}
                        onChange={(e) => setPizzaCustom(e.target.value)}
                        className="w-full p-2 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-orange-500/50 text-sm"
                      />
                    </div>
                  )}
                  
                  <div className="bg-white/5 p-2 rounded-lg">
                    <p className="text-[10px] text-rose-200/50 mb-1">SMS-Text:</p>
                    <p className="text-xs text-rose-200/80 italic">
                      &ldquo;{pizzaCount} mal {pizzaOrder === "Etwas anderes" ? (pizzaCustom || "___") : pizzaOrder}&rdquo;
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={orderPizza}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    Bestellen
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowPizzaModal(false);
                      setPizzaOrder("Sandro Spezial");
                      setPizzaCustom("");
                      setPizzaCount(1);
                    }}
                    variant="outline"
                    className="flex-1 bg-white/5 border-white/10"
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
